import type { FunctionComponent, ReactNode } from "react";
import { cn } from "./cn";

export type BadgeStatus = "default" | "success" | "processing" | "warning" | "error";

export interface BadgeProps {
  /** Number (capped at `overflowCount`) or custom node. Hidden at 0 unless `showZero`. */
  count?: ReactNode;
  overflowCount?: number;
  showZero?: boolean;
  /** Small dot instead of the count. */
  dot?: boolean;
  /** Standalone status dot (antd `status`); `text` renders beside it. */
  status?: BadgeStatus;
  text?: ReactNode;
  title?: string;
  className?: string;
  /** Wrapped element; the indicator sits on its top-right corner. */
  children?: ReactNode;
}

const STATUS_COLOR: Record<BadgeStatus, string> = {
  default: "bg-noa-text-disabled",
  success: "bg-noa-success",
  processing: "bg-noa-info animate-pulse",
  warning: "bg-noa-warning",
  error: "bg-noa-danger",
};

/** Count/dot indicator or status dot — Tailwind-only. */
export const Badge: FunctionComponent<BadgeProps> = ({
  count,
  overflowCount = 99,
  showZero,
  dot,
  status,
  text,
  title,
  className,
  children,
}) => {
  if (status) {
    return (
      <span className={cn("inline-flex items-center gap-2 align-middle", className)} title={title}>
        <span className={cn("inline-block size-1.5 rounded-full", STATUS_COLOR[status])} />
        {text && <span className="text-sm text-noa">{text}</span>}
      </span>
    );
  }

  const hidden = !dot && (count === undefined || count === null || (count === 0 && !showZero));
  const label = typeof count === "number" && count > overflowCount ? `${overflowCount}+` : count;

  const indicator = hidden ? null : (
    <sup
      title={title ?? (typeof count === "number" ? String(count) : undefined)}
      className={cn(
        "inline-flex items-center justify-center rounded-full bg-noa-danger font-normal text-noa-on-danger shadow-[0_0_0_1px_var(--bg)]",
        dot ? "size-1.5" : "h-5 min-w-5 px-1.5 text-xs leading-5",
        !!children && "absolute top-0 right-0 translate-x-1/2 -translate-y-1/2",
        !children && className
      )}
    >
      {!dot && label}
    </sup>
  );

  if (!children) return indicator;

  return (
    <span className={cn("relative inline-flex align-middle", className)}>
      {children}
      {indicator}
    </span>
  );
};
